import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Button, Stack, Alert, CircularProgress, Chip, Checkbox,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableFooter, IconButton,
  DialogTitle, DialogContent, DialogActions, TextField, MenuItem, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ImageIcon from '@mui/icons-material/Image';
import axios from 'axios';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from './firebaseConfig';
import { apiUrl, handleApiError } from './utils/api';
import NoMatchingRecords from './components/NoMatchingRecords';
import { useConfirm } from './components/ConfirmProvider';
import { useNotify } from './components/NotificationProvider';
import AppDialog from './components/AppDialog';
import { formatDate } from './utils/date';

// Canonical expense categories (value) + display label. Must match ExpenseReceiptService.normalizeCategory.
const CATEGORIES = [
  { value: 'FUEL', label: 'Fuel' },
  { value: 'TOLL', label: 'Toll' },
  { value: 'MAINT', label: 'Maintenance' },
  { value: 'OTHER', label: 'Other' },
];
const catLabel = (v) => (CATEGORIES.find((c) => c.value === v)?.label || v || '—');

const base = `${apiUrl}/api/expense-receipts`;
const money = (v) => (v == null || v === '' ? '—' : `$${Number(v).toFixed(2)}`);
const EMPTY = { category: 'FUEL', reviewStatus: 'VERIFIED' };

export default function FuelExpenses() {
  const [user] = useAuthState(auth);
  const [rows, setRows] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // '', PENDING, VERIFIED (client-side)
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // receipt being edited/added (dialog open when set)
  const [preview, setPreview] = useState(null); // receipt whose scan is being previewed
  const [selected, setSelected] = useState(() => new Set()); // selected receipt ids (bulk verify)
  const [applying, setApplying] = useState(false);
  const confirm = useConfirm();
  const notify = useNotify();

  const load = async () => {
    setLoading(true);
    setError('');
    setSelected(new Set()); // drop any selection so a bulk action can't hit rows no longer shown
    try {
      // Date range is applied server-side (only when BOTH ends are set); type + status are filtered
      // client-side below, so all three combine freely (the list endpoint treats date/category as
      // either-or). expense_receipt is tiny, so fetching the range and filtering in the browser is cheap.
      const params = {};
      if (startDate && endDate) { params.startDate = startDate; params.endDate = endDate; }
      const res = await axios.get(base, { params });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(handleApiError(err, 'Failed to load fuel & expense receipts'));
    } finally {
      setLoading(false);
    }
  };

  // Reload when the (complete) date range changes; a half-set range keeps the current list.
  useEffect(() => {
    if ((startDate && endDate) || (!startDate && !endDate)) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  // Type + status are filtered client-side so they combine with the date range.
  const visible = rows.filter((r) =>
    (!categoryFilter || r.category === categoryFilter)
    && (!statusFilter || r.reviewStatus === statusFilter));

  // Flag duplicate receipts. Two receipts are the "same" when their receipt # + date match (or, when
  // there's no receipt #, vendor + date + amount). Duplicates DOUBLE-COUNT in the Weekly Recon P&L, so
  // every row in a group of 2+ is flagged (over the whole loaded set, not just the filtered view) and
  // the extras should be deleted. Matches the backend scan dedup (findFirstByReceiptNoAndReceiptDate).
  const dupKey = (r) => {
    const d = r.receiptDate || '';
    const no = r.receiptNo == null ? '' : String(r.receiptNo).trim().toLowerCase();
    if (no) return `NO|${no}|${d}`;
    return `VA|${(r.vendor || '').trim().toLowerCase()}|${d}|${r.amount ?? ''}`;
  };
  const dupCounts = rows.reduce((m, r) => {
    const k = dupKey(r);
    m.set(k, (m.get(k) || 0) + 1);
    return m;
  }, new Map());
  const isDup = (r) => (dupCounts.get(dupKey(r)) || 0) > 1;
  // Redundant receipts = one per duplicate group can stay, the rest are extras.
  const dupExtra = [...dupCounts.values()].reduce((s, c) => s + (c > 1 ? c - 1 : 0), 0);

  // ---- multi-select bulk verify (mark many receipts VERIFIED at once instead of one by one) ----
  const toggleSel = (id) => setSelected((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const allSel = visible.length > 0 && visible.every((r) => selected.has(r.id));
  const someSel = visible.some((r) => selected.has(r.id)) && !allSel;
  const toggleSelAll = () => setSelected(allSel ? new Set() : new Set(visible.map((r) => r.id)));
  const clearSel = () => setSelected(new Set());
  const selectedPending = visible.filter((r) => selected.has(r.id) && r.reviewStatus !== 'VERIFIED');

  // Select the REDUNDANT copies (keep the lowest-id one per duplicate group), so "Delete selected"
  // removes the extras in one go without touching the copy you keep.
  const selectDuplicateExtras = () => {
    const kept = new Set();
    const extras = new Set();
    [...visible].sort((a, b) => (a.id || 0) - (b.id || 0)).forEach((r) => {
      if (!isDup(r)) return;
      const k = dupKey(r);
      if (!kept.has(k)) kept.add(k); else extras.add(r.id);
    });
    setSelected(extras);
  };

  const bulkVerify = async () => {
    // Only PENDING rows with a real amount can be verified; a $0/blank amount can't count toward the P&L.
    const toVerify = selectedPending.filter((r) => Number(r.amount) > 0);
    const noAmount = selectedPending.filter((r) => !(Number(r.amount) > 0));
    if (toVerify.length === 0) {
      notify.warning(noAmount.length
        ? 'Selected receipts need an amount before they can be verified.'
        : 'Nothing to verify — selected receipts are already verified.');
      return;
    }
    const ok = await confirm({
      title: 'Verify receipts',
      message: `Verify ${toVerify.length} receipt(s)? They'll count toward the Weekly Recon P&L.`,
      confirmText: 'Verify',
    });
    if (!ok) return;
    setApplying(true);
    const results = await Promise.allSettled(toVerify.map((r) =>
      axios.post(`${base}/${r.id}/verify`, null, { params: { email: user?.email } })));
    setApplying(false);
    const failed = results.filter((x) => x.status === 'rejected').length;
    if (failed === 0) notify.success(`Verified ${toVerify.length} receipt(s).`);
    else notify.warning(`Verified ${toVerify.length - failed} of ${toVerify.length}; ${failed} failed.`);
    if (noAmount.length) notify.warning(`${noAmount.length} skipped — no amount entered.`);
    clearSel();
    load();
  };

  const bulkDelete = async () => {
    const byId = new Map(visible.map((r) => [r.id, r]));
    const rows = [...selected].map((id) => byId.get(id)).filter(Boolean);
    if (rows.length === 0) { clearSel(); return; }
    const ok = await confirm({
      title: 'Delete receipts',
      message: `Delete ${rows.length} receipt(s)? This cannot be undone.`,
      confirmText: 'Delete',
      severity: 'error',
    });
    if (!ok) return;
    setApplying(true);
    const results = await Promise.allSettled(rows.map((r) => axios.delete(`${base}/${r.id}`)));
    setApplying(false);
    const failed = results.filter((x) => x.status === 'rejected').length;
    if (failed === 0) notify.success(`Deleted ${rows.length} receipt(s).`);
    else notify.warning(`Deleted ${rows.length - failed} of ${rows.length}; ${failed} failed.`);
    clearSel();
    load();
  };
  const total = visible.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const verifiedTotal = visible
    .filter((r) => r.reviewStatus === 'VERIFIED')
    .reduce((s, r) => s + (Number(r.amount) || 0), 0);

  const set = (k, v) => setEditing((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!editing.amount || Number(editing.amount) <= 0) {
      notify.warning('Enter the receipt amount before saving.');
      return;
    }
    try {
      if (editing.id) {
        await axios.put(`${base}/${editing.id}`, editing, { params: { email: user?.email } });
        notify.success('Receipt updated');
      } else {
        await axios.post(base, editing, { params: { email: user?.email } });
        notify.success('Expense added');
      }
      setEditing(null);
      load();
    } catch (err) {
      notify.error(handleApiError(err, 'Save failed'));
    }
  };

  const verify = async (row) => {
    if (!row.amount || Number(row.amount) <= 0) {
      notify.warning('Set the amount before verifying.');
      return;
    }
    try {
      await axios.post(`${base}/${row.id}/verify`, null, { params: { email: user?.email } });
      notify.success(`Receipt ${row.receiptNo || row.id} verified — it now counts toward the P&L.`);
      load();
    } catch (err) {
      notify.error(handleApiError(err, 'Verify failed'));
    }
  };

  const remove = async (row) => {
    const ok = await confirm({
      title: 'Delete receipt',
      message: `Delete ${catLabel(row.category)} receipt ${row.receiptNo || row.id} (${money(row.amount)})? This cannot be undone.`,
      confirmText: 'Delete',
      severity: 'error',
    });
    if (!ok) return;
    try {
      await axios.delete(`${base}/${row.id}`);
      notify.success('Receipt deleted');
      load();
    } catch (err) {
      notify.error(handleApiError(err, 'Delete failed'));
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1280, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom>Fuel &amp; Expenses</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Fuel, toll and maintenance receipts captured as operating <b>costs</b> — kept out of the haulage
        ticket flow so they never become invoice lines. Scanned receipts land here <b>Pending</b>; verify the
        amount and it feeds the week's expense on the <b>Weekly Recon</b> P&amp;L. Scan receipts on the Scan
        screen (fuel/expense receipts are detected automatically), or add one by hand below.
      </Typography>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap">
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing({ ...EMPTY })}>
          Add expense
        </Button>
        <TextField
          label="Start date" type="date" size="small" value={startDate}
          onChange={(e) => { setStartDate(e.target.value); clearSel(); }}
          InputLabelProps={{ shrink: true }} sx={{ width: 170 }}
        />
        <TextField
          label="End date" type="date" size="small" value={endDate}
          onChange={(e) => { setEndDate(e.target.value); clearSel(); }}
          InputLabelProps={{ shrink: true }} sx={{ width: 170 }}
        />
        {(startDate || endDate) && (
          <Button size="small" color="inherit"
            onClick={() => { setStartDate(''); setEndDate(''); clearSel(); }}>
            Clear dates
          </Button>
        )}
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap">
        <ToggleButtonGroup
          size="small" exclusive value={categoryFilter}
          onChange={(e, v) => { if (v !== null) { setCategoryFilter(v); clearSel(); } }}
        >
          <ToggleButton value="">All types</ToggleButton>
          {CATEGORIES.map((c) => <ToggleButton key={c.value} value={c.value}>{c.label}</ToggleButton>)}
        </ToggleButtonGroup>
        <ToggleButtonGroup
          size="small" exclusive value={statusFilter}
          onChange={(e, v) => { if (v !== null) { setStatusFilter(v); clearSel(); } }}
        >
          <ToggleButton value="PENDING">Pending</ToggleButton>
          <ToggleButton value="VERIFIED">Verified</ToggleButton>
          <ToggleButton value="">All</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="body2" color="text.secondary">({visible.length})</Typography>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }} onClose={() => setError('')}>{error}</Alert>}
      {loading && <CircularProgress />}
      {!loading && visible.length === 0 && <NoMatchingRecords />}

      {dupExtra > 0 && (
        <Alert
          severity="warning" sx={{ mb: 1.5 }}
          action={<Button color="inherit" size="small" onClick={selectDuplicateExtras}>Select duplicates</Button>}
        >
          {dupExtra} duplicate receipt{dupExtra === 1 ? '' : 's'} detected (same receipt&nbsp;# &amp; date).
          Verified duplicates <b>double-count</b> in the Weekly Recon P&amp;L — review and delete the extras.
        </Alert>
      )}

      {selected.size > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: '#FFF8E1', borderColor: '#F5D68A' }}>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
              {selected.size} selected{selectedPending.length !== selected.size
                ? ` · ${selectedPending.length} pending` : ''}
            </Typography>
            <Button
              variant="contained" color="success" startIcon={<CheckCircleIcon />}
              disabled={applying || selectedPending.length === 0} onClick={bulkVerify}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {applying ? 'Verifying…' : `Verify selected (${selectedPending.length})`}
            </Button>
            <Button
              variant="outlined" color="error" startIcon={<DeleteIcon />}
              disabled={applying} onClick={bulkDelete} sx={{ whiteSpace: 'nowrap' }}
            >
              {`Delete selected (${selected.size})`}
            </Button>
            <Button color="inherit" onClick={clearSel} sx={{ whiteSpace: 'nowrap' }}>Clear</Button>
          </Stack>
        </Paper>
      )}

      {visible.length > 0 && (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox size="small" checked={allSel} indeterminate={someSel} onChange={toggleSelAll} />
                </TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Vendor</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Receipt #</TableCell>
                <TableCell>Truck</TableCell>
                <TableCell align="right">Gallons</TableCell>
                <TableCell align="right">$/gal</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((r) => (
                <TableRow key={r.id} hover selected={selected.has(r.id)}>
                  <TableCell padding="checkbox">
                    <Checkbox size="small" checked={selected.has(r.id)} onChange={() => toggleSel(r.id)} />
                  </TableCell>
                  <TableCell>{formatDate(r.receiptDate) || '—'}</TableCell>
                  <TableCell>{r.vendor || '—'}</TableCell>
                  <TableCell><Chip size="small" variant="outlined" label={catLabel(r.category)} /></TableCell>
                  <TableCell>{r.receiptNo || '—'}</TableCell>
                  <TableCell>{r.truckNo || '—'}</TableCell>
                  <TableCell align="right">{r.gallons != null ? Number(r.gallons).toFixed(3) : '—'}</TableCell>
                  <TableCell align="right">{r.pricePerGallon != null ? Number(r.pricePerGallon).toFixed(3) : '—'}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{money(r.amount)}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Chip size="small" label={r.reviewStatus}
                        color={r.reviewStatus === 'VERIFIED' ? 'success' : 'warning'} />
                      {isDup(r) && (
                        <Chip size="small" color="error" variant="outlined" label="Duplicate"
                          title="Same receipt # & date as another row — double-counts in the P&L" />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                    {r.hasImage && (
                      <IconButton size="small" onClick={() => setPreview(r)} title="View scan">
                        <ImageIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton size="small" onClick={() => setEditing(r)} title="Edit"><EditIcon fontSize="small" /></IconButton>
                    {r.reviewStatus !== 'VERIFIED' && (
                      <IconButton size="small" color="success" onClick={() => verify(r)} title="Verify">
                        <CheckCircleIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton size="small" color="error" onClick={() => remove(r)} title="Delete"><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={8} align="right" sx={{ fontWeight: 600 }}>Total shown</TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>${total.toFixed(2)}</TableCell>
                <TableCell colSpan={2} sx={{ color: 'text.secondary', fontSize: 12 }}>
                  ${verifiedTotal.toFixed(2)} verified (counts toward P&amp;L)
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      )}

      {/* Add / edit dialog */}
      <AppDialog open={!!editing} onClose={() => setEditing(null)} fullWidth maxWidth="sm">
        <DialogTitle>{editing?.id ? 'Edit receipt' : 'Add expense'}</DialogTitle>
        <DialogContent>
          {editing && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Stack direction="row" spacing={2}>
                <TextField label="Date" type="date" value={editing.receiptDate ?? ''} onChange={(e) => set('receiptDate', e.target.value)} InputLabelProps={{ shrink: true }} fullWidth size="small" />
                <TextField label="Type" select value={editing.category ?? 'FUEL'} onChange={(e) => set('category', e.target.value)} fullWidth size="small">
                  {CATEGORIES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                </TextField>
              </Stack>
              <TextField label="Vendor" value={editing.vendor ?? ''} onChange={(e) => set('vendor', e.target.value)} fullWidth size="small" placeholder="e.g. EXXON EXPRESS PAY" />
              <Stack direction="row" spacing={2}>
                <TextField label="Amount ($)" type="number" value={editing.amount ?? ''} onChange={(e) => set('amount', e.target.value)} fullWidth size="small" required />
                <TextField label="Receipt #" value={editing.receiptNo ?? ''} onChange={(e) => set('receiptNo', e.target.value)} fullWidth size="small" />
              </Stack>
              {editing.category === 'FUEL' && (
                <Stack direction="row" spacing={2}>
                  <TextField label="Gallons" type="number" value={editing.gallons ?? ''} onChange={(e) => set('gallons', e.target.value)} fullWidth size="small" />
                  <TextField label="$/gallon" type="number" value={editing.pricePerGallon ?? ''} onChange={(e) => set('pricePerGallon', e.target.value)} fullWidth size="small" />
                </Stack>
              )}
              <Stack direction="row" spacing={2}>
                <TextField label="Truck #" value={editing.truckNo ?? ''} onChange={(e) => set('truckNo', e.target.value)} fullWidth size="small" />
                <TextField label="Payment" value={editing.paymentMethod ?? ''} onChange={(e) => set('paymentMethod', e.target.value)} fullWidth size="small" placeholder="e.g. Visa 5885" />
              </Stack>
              <TextField label="Notes" value={editing.notes ?? ''} onChange={(e) => set('notes', e.target.value)} fullWidth size="small" multiline minRows={2} />
              {editing.hasImage && (
                <img src={`${base}/${editing.id}/image`} alt="receipt scan"
                  style={{ width: '100%', border: '1px solid #ddd', borderRadius: 4 }} />
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button variant="outlined" color="success" onClick={save}>Save</Button>
          {editing?.id && editing?.reviewStatus !== 'VERIFIED' && (
            <Button variant="contained" color="success" onClick={async () => { await save(); await verify(editing); }}>
              Save &amp; Verify
            </Button>
          )}
        </DialogActions>
      </AppDialog>

      {/* Scan preview dialog */}
      <AppDialog open={!!preview} onClose={() => setPreview(null)} fullWidth maxWidth="sm">
        <DialogTitle>Receipt scan {preview?.receiptNo ? `#${preview.receiptNo}` : ''}</DialogTitle>
        <DialogContent>
          {preview && (
            <img src={`${base}/${preview.id}/image`} alt="receipt scan"
              style={{ width: '100%', border: '1px solid #ddd', borderRadius: 4 }} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreview(null)}>Close</Button>
        </DialogActions>
      </AppDialog>
    </Box>
  );
}
